import React from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
} from 'reactstrap'
import styled from 'styled-components'
import moment from '../../../lib/moment.js'
import get from 'lodash/get'
import find from 'lodash/find'
import uniq from 'lodash/uniq'
import { FONTS, COLORS } from '../../../constants'

const renderHeader = (selectedResource, resource_util) => {
  let prevYear = ''
  return (
    <HeaderRow noGutters>
      <Col sm={ 5 }>
        <WrapperRow style={ { fontFamily: FONTS.GOTHAM_MEDIUM } }>
          <IDCol>
            <ResourceName>{ get(selectedResource, 'emp_code') }</ResourceName>
            <div className='tooltip'>{ `${get(selectedResource, 'emp_code')}` }</div>
          </IDCol>
          <NameCol>
            <ResourceName>{ `${get(selectedResource, 'first_name')} ${get(selectedResource, 'last_name')}` }</ResourceName>
            <span>{ ` (${get(selectedResource, 'nick_name')})` }</span>
            <div className='tooltip'>{ `${get(selectedResource, 'first_name')} ${get(selectedResource, 'last_name')} (${get(selectedResource, 'nick_name')})` }</div>
          </NameCol>
          <InfoCol>
            <ResourceName>{ get(selectedResource, 'level') }</ResourceName>
            <div className='tooltip'>{ `${get(selectedResource, 'level')}` }</div>
          </InfoCol>
        </WrapperRow>
      </Col>
      <Col sm={ 7 }>
        <YearRow
          style={ {
            fontFamily: FONTS.GOTHAM_MEDIUM ,
            borderTop: '1px solid #CCCCCC',
            borderRight: '1px solid #CCCCCC',
          } }
        >
          { resource_util.map(year => {
            const notEqual = moment(get(year, 'month'), 'YYYY-MM').year() !== prevYear
            if (notEqual) {
              prevYear = moment(get(year, 'month'), 'YYYY-MM').year()
              return (
                <MonthCol style={ { borderLeft: notEqual ? '1px solid #CCCCCC' : '' } }>
                  { moment(get(year, 'month'), 'YYYY-MM').year() }
                </MonthCol>
              )
            }
            return <MonthCol />
          }) }
        </YearRow>
        <MonthRow style={ { backgroundColor: '#EDEDED', borderRight: '1px solid #CCCCCC' } }>
          { resource_util.map(month => {
            return (
              <MonthCol style={ { borderLeft: '1px solid #CCCCCC' } }>
                { moment(get(month, 'month'), 'YYYY-MM').format('MMM') }
              </MonthCol>
            )
          }) }
        </MonthRow>
      </Col>
    </HeaderRow>
  )
}

const renderTotalSummary = (selectedResource, currentMonth) => {
  const resource_util = get(selectedResource, 'resource_util', [])
  const hiring_date = get(selectedResource, 'hiring_date')
  const last_effective_date = get(selectedResource, 'last_effective_date')

  return (
    <SummaryRow
      noGutters
      style={ { fontFamily: FONTS.GOTHAM_MEDIUM } }
    >
      <Col sm={ 5 }>
        <TotalText>Total Summary</TotalText>
      </Col>
      <Col sm={ 7 }>
        <MonthRow style={ { borderRight: '1px solid #CCCCCC' } }>
          { resource_util.map(summary => {
            const month = moment(get(summary, 'month')).month()
            let color
            if (get(summary, 'value', 0) > 1) color = 'rgba(212, 75, 73, 0.45)'
            else if (get(summary, 'value', 0) < 0.7 && get(summary, 'value', 0) > 0) color = 'rgba(240, 173, 78, 0.45)'
            else if (get(summary, 'value', 0) >= 0.7 && get(summary, 'value', 0) <= 1) color = 'rgba(181, 232, 181, 1)'
            const style = {
              width: '70%',
              borderRadius: '10px 10px 10px 10px',
              backgroundColor: color,
              textAlign: 'center',
            }
            let showWarning = false
            let warningMessage = ''
            const name = `${get(selectedResource, 'first_name')} ${get(selectedResource, 'last_name')} (${get(selectedResource, 'nick_name')})`
            if (last_effective_date === null) {
              showWarning = moment(get(summary, 'month')).isBefore(moment(hiring_date))
              warningMessage = `${name} hiring date is ${moment(hiring_date).format('DD/MM/YYYY')}`
            } else {
              showWarning = moment(get(summary, 'month')).isAfter(moment(last_effective_date))
              warningMessage = `${name} last effective date is ${moment(last_effective_date).format('DD/MM/YYYY')}`
            }

            return (
              <SummaryCol style={ { backgroundColor: month === currentMonth ? '#EDEFF5' : '' } }>
                <WarningWrapper>
                  { showWarning && <Circle /> }
                  <span style={ color ? style : {} }>
                    { parseFloat(get(summary, 'value', 0)).toFixed(2) }
                  </span>
                </WarningWrapper>
                { showWarning &&
                  <div className='tooltip' style={ { width: 200 } }>{ warningMessage }</div>
                }
              </SummaryCol>
            )
          }) }
        </MonthRow>
      </Col>
    </SummaryRow>
  )
}

const renderResourceHeader = (resource_util, currentMonth) => {
  return (
    <ResourceRow noGutters>
      <Col sm={ 5 }>
        <WrapperRow style={ { height: 20, backgroundColor: '#F1F1F1' } }>
          <IDCol>{ 'ID' }</IDCol>
          <ProjNameCol>{ 'Project Name' }</ProjNameCol>
          <LevelCol>{ 'Type' }</LevelCol>
          <ManDaysCol>{ 'Man-months' }</ManDaysCol>
        </WrapperRow>
      </Col>
      <Col sm={ 7 }>
        <MonthRow style={ { height: 20 } }>
          { resource_util.map((util, key) => {
            const showBorder = key === 0
            const month = moment(get(util, 'month')).month()
            return (
              <MonthCol style={ {
                borderLeft: showBorder ? '1px solid #CCCCCC' : '',
                backgroundColor: month === currentMonth ? '#EDEFF5' : '#F1F1F1',
              } }
              />
            )
          }) }
        </MonthRow>
      </Col>
    </ResourceRow>
  )
}

const renderResource = (project, selectedResource, currentMonth) => {
  const resource_util = get(selectedResource, 'resource_util', [])
  const sum_man_month_by_project = get(selectedResource, 'sum_man_month_by_project', [])
  const sum_man_month = get(sum_man_month_by_project, [get(project, '_id')], 0)
  return (
    <ResourceRow noGutters>
      <Col sm={ 5 }>
        <WrapperRow style={ { height: 40 } }>
          <IDCol>
            <ResourceName>{ get(project, 'project_code') }</ResourceName>
            <div className='tooltip'>{ `${get(project, 'project_code', '-')}` }</div>
          </IDCol>
          <ProjNameCol>
            <ResourceName>{ get(project, 'name') }</ResourceName>
            <div className='tooltip'>{ `${get(project, 'name', '-')}` }</div>
          </ProjNameCol>
          <LevelCol>
            <ResourceName>{ get(project, 'project_type', '-') }</ResourceName>
            <div className='tooltip'>{ get(project, 'project_type', '-') }</div>
          </LevelCol>
          <ManDaysCol>
            <div>{ parseFloat(sum_man_month).toFixed(2) }</div>
          </ManDaysCol>
        </WrapperRow>
      </Col>
      <Col sm={ 7 }>
        <MonthRow style={ { height: 40 } }>
          { resource_util.map((util, key) => {
            const proj = find(get(util, 'projects', []), {
              project_id: get(project, '_id'),
            })
            const month = moment(get(util, 'month')).month()
            const showBorder = month === 3 || month === 6
            || month === 9 || month === 0 || key === 0
            return (
              <MonthCol style={ {
                borderLeft: showBorder ? '1px solid #CCCCCC' : '',
                backgroundColor: month === currentMonth ? '#EDEFF5' : '',
              } }
              >
                <div>
                  { parseFloat(get(proj, 'value', 0)).toFixed(2) }
                </div>
              </MonthCol>
            )
          }) }
        </MonthRow>
      </Col>
    </ResourceRow>
  )
}

export const ShowResourceDetail = (props) => {
  const { open, toggle } = props
  const userID = get(props, 'userID')
  const efficiencyResource = get(props, 'efficiencyResource')
  const month = moment(get(efficiencyResource, 'efficiencyResource.month')).month()
  const is_submited = get(efficiencyResource, 'efficiencyResource.is_submited')
  const projectAdditionalList = is_submited ?
    get(efficiencyResource, 'efficiencyResource.efficiency_resource_snapshot.projectAdditionalList', []) : get(efficiencyResource, 'projectAdditionalList')
  const base_projects = is_submited ?
    get(efficiencyResource, 'efficiencyResource.efficiency_resource_snapshot.base_project', []) : get(efficiencyResource, 'efficiencyResource.base_project')
  const allProjects = [...base_projects, ...projectAdditionalList]
  const resourceUtilization = is_submited ?
    get(efficiencyResource, 'efficiencyResource.efficiency_resource_snapshot.resourceUtilization', []) :
    get(efficiencyResource, 'resourceUtilization', [])
  const resource_util = get(resourceUtilization, ['0', 'resource_util'], [])
  const selectedResource = find(resourceUtilization, {
    user_id: userID,
  })
  let projectIDs = []
  get(selectedResource, 'resource_util', []).forEach(res => {
    if (get(res, 'projects.length') > 0) {
      get(res, 'projects').forEach(project => projectIDs.push(get(project, 'project_id')))
    }
  })
  projectIDs = uniq(projectIDs)
  return (
  /* eslint no-underscore-dangle: 0 */
    <Modal
      size='xl'
      isOpen={ open }
      toggle={ () => toggle() }
    >
      <Header toggle={ () => toggle() }>
        <HeadLabel>{ `Resource Utilization by projects` }</HeadLabel>&nbsp;
        <Remark>(Total summary numbers will be calculated to rounded number.)</Remark>
      </Header>
      <CustomBody>
        <Row noGutters>
          <Col>
            { renderHeader(selectedResource, resource_util) }
            { renderTotalSummary(selectedResource, month) }
            { renderResourceHeader(get(selectedResource, 'resource_util', []), month) }
            { projectIDs.map(id => {
              const project = find(allProjects, { _id: id })
              return renderResource(project, selectedResource, month)
            }) }
          </Col>
        </Row>
      </CustomBody>
    </Modal>
  )
}

ShowResourceDetail.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
}
ShowResourceDetail.defaultProps = {
  open: false,
  toggle: () => {},
}

const CustomBody = styled(ModalBody)`
  height: calc(100vh - 150px);
  max-height: calc(100vh - 150px);
  overflow-y: scroll;
  overflow-x: hidden;
  font-family: ${FONTS.GOTHAM} !important;
  font-size: ${FONTS.SMALL_SIZE};
  cursor: default;

  .tooltip {
    width: 50%;
    max-width: 200px;
    font-size: 12px;
    top: 30px;
    background-color: #fff;
    color: black;
    padding: 5px;
    box-shadow: 0 1px 3px 0 rgba(0,0,0,0.3);
    transition: opacity 1s ease;
    opacity: 0;
    visibility: hidden;
  }
  
  span:hover + .tooltip,
  .tooltip:hover,
  span:hover ~ span + .tooltip,
  .tooltip:hover  {
    transition: opacity 0s ease;
    transition-delay: 1s;
    opacity: 1;
    visibility: visible;
  }
  p:hover + .tooltip,
  .tooltip:hover {
    transition: opacity 0s ease;
    transition-delay: 0s;
    opacity: 1;
    visibility: visible;
  }
`
const Header = styled(ModalHeader)`
  font-family: ${FONTS.GOTHAM_MEDIUM};
  font-size: ${FONTS.SMALL_SIZE};
`
const WrapperRow = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  border-bottom: 1px solid #CCCCCC;
`
const HeaderRow = styled(Row)`
  height: 50px;
  background-color: #404040;
  color: white;
  border-bottom: none;
`
const SummaryRow = styled(Row)`
  height: 25px;
  background-color: #CCCCCC;
`
const MonthRow = styled(WrapperRow)`
  height: 25px;
  color: black;
  justify-content: space-around;
  align-items: center;
`
const IDCol = styled.div`
  height: 100%;
  width: 20%;
  display: flex;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
`
const NameCol = styled.div`
  height: 100%;
  width: 55%;
  display: flex;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
`
const ProjNameCol = styled(NameCol)`
  width: 40%;
`
const InfoCol = styled.div`
  height: 100%;
  width: 25%;
  display: flex;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
  overflow: hidden;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`
const LevelCol = styled(InfoCol)`
  width: 20%;
`
const ManDaysCol = styled(InfoCol)`
  width: 20%;
`
const YearRow = styled(MonthRow)`
  background-color: white;
`
const MonthCol = styled.div`
  height: 100%;
  width: 100px;
  max-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const ResourceRow = styled(Row)`
  border-left: 1px solid #CCCCCC;
  border-right: 1px solid #CCCCCC;
  :hover {
    background-color: #E5E5E5;
  }
`
const ResourceName = styled.span`
  width: 80%;
  white-space: nowrap;
  overflow: hidden;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`
const TotalText = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  margin-left: 10px;
`
const SummaryCol = styled(MonthRow)`
  background-color: #CCCCCC;
  border-left: 1px solid rgba(0, 0, 0, 0.16);
  position: relative;
`
const HeadLabel = styled.span`
  font-family: ${FONTS.GOTHAM_MEDIUM};
  font-size: ${FONTS.TITLE_SIZE};
  color: ${COLORS.PRIMARY_RED};
`
const Remark = styled.span`
  font-family: ${FONTS.GOTHAM};
  font-size: ${FONTS.SMALL_SIZE};
`
const Circle = styled.span`
  font-size: 10px;
  text-align: center;
  height: 8px;
  width: 8px;
  background-color: #D44B49;
  border-radius: 50%;
  cursor: default;
  position: absolute;
  top: 10%;
  right: 10%;
  z-index: 1;
`
const WarningWrapper = styled.p`
  font-family: ${FONTS.GOTHAM_MEDIUM} !important;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
`



// WEBPACK FOOTER //
// ./src/components/efficiency-resource/form/show-resource-detail.js